import { ShoppingCart } from '@mui/icons-material';
import { usePaymentRedirect } from '@01bee-npm-registry/worldline-react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { Price } from 'apis/price';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StateType } from 'store/reducers';
import { localizePrice } from 'utils/numbers';

export interface PaymentOptionProps {
    trial?: boolean;
    price: number;
    discountedPrice?: number;
    vattedPrice: number;
    vat: number;
    months: number;
    priceList: Price;
    disabled?: boolean;
}

const PaymentOption: React.FC<PaymentOptionProps> = ({
    trial = false,
    price = 0,
    discountedPrice,
    vattedPrice = 0,
    vat = 0,
    months = 1,
    priceList,
    disabled = false
}): JSX.Element => {
    const { t } = useTranslation();
    const { accessToken } = useSelector((state: StateType) => state.auth);
    const amount = useMemo(() => Math.max(discountedPrice ? discountedPrice : price, 0), [discountedPrice, price]);
    const years = useMemo(() => Math.floor(months / 12), [months]);
    const yearMonths = useMemo(() => months % 12, [months]);

    const yearsLabel = useMemo(() => (years ? `${years} ${t('year', { count: years })}` : ''), [t, years]);
    const yearMonthsLabel = useMemo(() => (yearMonths ? `${yearMonths} ${t('month', { count: yearMonths })}` : ''), [t, yearMonths]);

    const { trigger } = usePaymentRedirect({
        endpoint: `${process.env.REACT_APP_SERVER_URL || 'http://localhost:8080'}/payments/worldline/new-payment`,
        licenseId: process.env.REACT_APP_WORLDLINE_LICENSE_ID ?? '',
        worldlineURL: process.env.REACT_APP_WORLDLINE_URL ?? '',
        headers: {
            Authorization: `Bearer ${accessToken ?? ''}`
        },
        body: {
            months,
            urlDone: `${process.env.REACT_APP_URL ?? 'http://localhost:3000'}/license/renew?AMOUNT=${
                vattedPrice ?? discountedPrice ?? price
            }`,
            urlError: `${process.env.REACT_APP_URL ?? 'http://localhost:3000'}/license/renew`
        }
    });

    const handleClick = () => {
        if (accessToken && !disabled) {
            trigger().catch((error) => console.error(error));
        }
    };

    return (
        <Paper>
            <Stack p={2} spacing={2}>
                <Stack direction="row" alignItems="baseline">
                    <Typography variant="h3">
                        {!trial ? '+ ' : ''}
                        {years && yearMonths ? t('x_and_y', { x: yearsLabel, y: yearMonthsLabel }) : yearsLabel || yearMonthsLabel || ''}
                    </Typography>
                </Stack>
                {
                    <Typography variant="h6" color="textSecondary" my={1}>
                        {discountedPrice &&
                            discountedPrice < price &&
                            priceList.discount?.find((row) => row.months === months)?.description?.it}
                    </Typography>
                }
                <Stack alignItems="end">
                    <Stack direction="row" alignItems="baseline" justifyContent="end" spacing={2}>
                        {discountedPrice && discountedPrice < price && (
                            <Typography variant="h6" color="textSecondary" style={{ textDecoration: 'line-through' }} my={1}>
                                {localizePrice(price)} €
                            </Typography>
                        )}
                        <Typography variant="h4">{localizePrice(amount)} €</Typography>
                    </Stack>
                    <Typography variant="h6">
                        +{t('vat')?.toUpperCase() || ''} {vat} %
                    </Typography>
                    <Typography variant="h3">
                        {t('total')}: {localizePrice(vattedPrice)} €
                    </Typography>
                </Stack>
                <Button variant="outlined" startIcon={<ShoppingCart />} onClick={handleClick} disabled={disabled}>
                    {t(trial ? 'license_subscribe_action' : 'license_renew_action')}
                </Button>
            </Stack>
        </Paper>
    );
};

export default PaymentOption;
